// Claymation Ad's parts of the script editor (live's `script-summary`, `shot-header`,
// `spoken-line-row` and `end-card-fields`; spec G14-claymation-ad, PR d1), in the shared script
// section's slots: the summary above the lines, each shot's header and picture before the line it
// opens, each line's spoken text ("Says:") after it, and the end card's headline and support line
// after the lines.
import { Text, Textarea } from "@mantine/core";
import { Clapperboard } from "lucide-react";
import type { ReactNode } from "react";

import type { ClaymationScript } from "../../../api/generated/model";
import {
  claymationEndCardHeadlineMax,
  claymationEndCardSupportLineMax,
  claymationLineSpokenMax,
  claymationShotPictureMax,
} from "../../../api/generated/zod";
import { scriptSummary, shotHeader, shotOpenedAt } from "./claymation-script";
import classes from "./claymation.module.css";

/** Live's words (`library-view/constants.ts`). */
const SAYS = "Says:";
const SAYS_PLACEHOLDER = "What the narrator says";
const PICTURE_PLACEHOLDER = "What the clay shot shows";
const END_CARD = "End card";
const END_CARD_FIELDS = [
  { field: "headline", label: "Headline", placeholder: "The product name", maxLength: claymationEndCardHeadlineMax },
  {
    field: "support_line",
    label: "Support line",
    placeholder: "One short line from the ad",
    maxLength: claymationEndCardSupportLineMax,
  },
] as const;

interface FieldProps {
  label: string;
  value: string;
  placeholder: string;
  maxLength: number;
  disabled: boolean;
  onChange: (value: string) => void;
}

/** One of the script's own fields, typed in place as a line is (live's `ScriptLineInput`). */
function Field({ label, value, placeholder, maxLength, disabled, onChange }: FieldProps) {
  return (
    <Textarea
      className={classes.field}
      classNames={{ input: classes.fieldInput }}
      variant="unstyled"
      autosize
      minRows={1}
      aria-label={label}
      placeholder={placeholder}
      maxLength={maxLength}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );
}

export interface ScriptParts {
  head: ReactNode;
  lineHead: (index: number) => ReactNode;
  lineFoot: (index: number) => ReactNode;
  foot: ReactNode;
}

/** The parts for `script`; `onChange` takes each edit as a change to the script. */
export function scriptParts(
  script: ClaymationScript,
  locked: boolean,
  onChange: (change: (script: ClaymationScript) => ClaymationScript) => void,
): ScriptParts {
  const head = (
    <div className={classes.summary}>
      {scriptSummary(script).map((line) => (
        <Text key={line} className={classes.summaryLine}>
          {line}
        </Text>
      ))}
    </div>
  );
  const lineHead = (index: number) => {
    const shot = shotOpenedAt(script, index);
    if (shot === null) return null;
    return (
      <div className={classes.shot}>
        <Text className={classes.shotTitle}>{shotHeader(script, shot)}</Text>
        <div className={classes.row}>
          <Clapperboard size={12} className={classes.shotIcon} aria-hidden />
          <Field
            label={`Shot ${shot.shot_id} picture`}
            value={shot.picture}
            placeholder={PICTURE_PLACEHOLDER}
            maxLength={claymationShotPictureMax}
            disabled={locked}
            onChange={(picture) =>
              onChange((current) => ({
                ...current,
                shots: current.shots.map((each) => (each.shot_id === shot.shot_id ? { ...each, picture } : each)),
              }))
            }
          />
        </div>
      </div>
    );
  };
  const lineFoot = (index: number) => (
    <div className={classes.row}>
      <Text className={classes.label}>{SAYS}</Text>
      <Field
        label={`Line ${index + 1} says`}
        value={script.lines[index]?.spoken ?? ""}
        placeholder={SAYS_PLACEHOLDER}
        maxLength={claymationLineSpokenMax}
        disabled={locked}
        onChange={(spoken) =>
          onChange((current) => ({
            ...current,
            lines: current.lines.map((line, at) => (at === index ? { ...line, spoken } : line)),
          }))
        }
      />
    </div>
  );
  const foot = (
    <div className={classes.endCard}>
      <Text className={classes.endCardTitle}>{END_CARD}</Text>
      {END_CARD_FIELDS.map(({ field, label, placeholder, maxLength }) => (
        <div key={field} className={classes.row}>
          <Text className={classes.label}>{label}</Text>
          <Field
            label={`${END_CARD} ${label.toLowerCase()}`}
            value={script.end_card[field]}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={locked}
            onChange={(value) =>
              onChange((current) => ({ ...current, end_card: { ...current.end_card, [field]: value } }))
            }
          />
        </div>
      ))}
    </div>
  );
  return { head, lineHead, lineFoot, foot };
}
